import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Badge,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CardMedia,
  Slider,
  CardActionArea,
} from "@mui/material";
import {
  Search as SearchIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  Send as ApplyIcon,
  Company as CompanyIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import Cookies from "js-cookie";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3500";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s ease-in-out",
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
}));

const CompanyAvatar = styled(Avatar)(({ theme }) => ({
  width: 56,
  height: 56,
  backgroundColor: theme.palette.primary.main,
  fontSize: "1.5rem",
  fontWeight: 600,
}));

const SkillChip = styled(Chip)(({ theme, level }) => ({
  margin: theme.spacing(0.5),
  backgroundColor:
    level === "Expert"
      ? theme.palette.error.light
      : level === "Intermediate"
      ? theme.palette.warning.light
      : theme.palette.success.light,
  color:
    level === "Expert"
      ? theme.palette.error.contrastText
      : level === "Intermediate"
      ? theme.palette.warning.contrastText
      : theme.palette.success.contrastText,
  fontWeight: 500,
  fontSize: "0.75rem",
}));

const SearchJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFunction, setSelectedFunction] = useState("");
  const [selectedEmploymentType, setSelectedEmploymentType] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [favorites, setFavorites] = useState(new Set());
  const [selectedJob, setSelectedJob] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [functions, setFunctions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyJobsDialogOpen, setCompanyJobsDialogOpen] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  const token = Cookies.get("token");

  useEffect(() => {
    fetchJobs();
    fetchFilters();
    fetchCompanyProfiles();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      console.log(
        "[SEARCH] Fetching jobs from:",
        `${API_BASE}/api/job-postings/search`
      );

      const response = await fetch(`${API_BASE}/api/job-postings/search`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("[SEARCH] Response status:", response.status);
      console.log("[SEARCH] Response headers:", response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[SEARCH] Response error:", errorText);
        throw new Error(
          `Failed to fetch jobs: ${response.status} ${errorText}`
        );
      }

      const data = await response.json();
      console.log("[SEARCH] Received jobs data:", data);
      setJobs(data);
    } catch (err) {
      console.error("[SEARCH] Error fetching jobs:", err);
      setError("Failed to load job postings. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      // Fetch job functions
      const functionsResponse = await fetch(
        `${API_BASE}/api/profile/job-functions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (functionsResponse.ok) {
        const functionsData = await functionsResponse.json();
        setFunctions(functionsData);
      }

      // Try to fetch countries, but don't fail if the endpoint doesn't exist
      try {
        const locationsResponse = await fetch(
          `${API_BASE}/api/profile/countries`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (locationsResponse.ok) {
          const locationsData = await locationsResponse.json();
          setLocations(locationsData);
        }
      } catch (err) {
        console.warn(
          "Countries endpoint not available, continuing without location filter"
        );
        // Set some default countries for testing
        setLocations([
          { id: 1, name: "Switzerland", code: "CH" },
          { id: 2, name: "Germany", code: "DE" },
          { id: 3, name: "United States", code: "US" },
          { id: 4, name: "United Kingdom", code: "GB" },
        ]);
      }
    } catch (err) {
      console.error("Error fetching filters:", err);
    }
  };

  const fetchCompanyProfiles = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/job-postings/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyProfiles(data);
      }
    } catch (err) {
      console.error("Error fetching company profiles:", err);
    }
  };

  const handleFavorite = (jobId) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(jobId)) {
        newFavorites.delete(jobId);
      } else {
        newFavorites.add(jobId);
      }
      return newFavorites;
    });
  };

  const handleJobClick = async (job) => {
    setSelectedJob(job);
    setDialogOpen(true);
    setHasApplied(false);
    try {
      const response = await fetch(
        `${API_BASE}/api/job-postings/${job.id}/applied`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setHasApplied(data.applied);
      }
    } catch (err) {
      setHasApplied(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedJob(null);
  };

  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
    setCompanyJobsDialogOpen(true);
  };

  const handleCloseCompanyJobsDialog = () => {
    setCompanyJobsDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleApply = async (jobId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/job-postings/${jobId}/apply`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        setAppliedJobs((prev) => new Set([...prev, jobId]));
        setHasApplied(true);
        // Show success message or update UI
      }
    } catch (err) {
      console.error("Error applying for job:", err);
    }
  };

  const getFilteredJobs = () => {
    return jobs.filter((job) => {
      const matchesSearch =
        !searchTerm ||
        job.job_role_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.job_function_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        job.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.summary?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFunction =
        !selectedFunction || job.job_function_id === parseInt(selectedFunction);
      const matchesEmployment =
        !selectedEmploymentType ||
        job.employment_type === selectedEmploymentType;
      const matchesLocation =
        !selectedLocation || job.country_id === parseInt(selectedLocation);

      return (
        matchesSearch && matchesFunction && matchesEmployment && matchesLocation
      );
    });
  };

  const getCompanyInitials = (companyName) => {
    if (!companyName) return "?";
    return companyName
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getEmploymentTypeColor = (type) => {
    switch (type) {
      case "Full-time":
        return "success";
      case "Part-time":
        return "warning";
      case "Contractor":
        return "info";
      case "Freelancer":
        return "secondary";
      default:
        return "default";
    }
  };

  const renderSkills = (skills) => {
    if (!skills) return null;

    // Combine must-have, should-have, and could-have (ignore "specific" ones)
    const allSkills = [
      ...(skills.selected_must_have_skills || []),
      ...(skills.selected_should_have_skills || []),
      ...(skills.selected_could_have_skills || []),
    ].flatMap((skillGroup) => {
      const skillName = Object.keys(skillGroup)[0];
      const skillData = skillGroup[skillName];
      return {
        skill: skillName,
        level: skillData.level,
      };
    });

    return (
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Required Skills:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {allSkills.slice(0, 5).map((skill, index) => (
            <SkillChip
              key={index}
              label={`${skill.skill} (${skill.level})`}
              size="small"
              level={skill.level}
            />
          ))}
          {allSkills.length > 5 && (
            <Chip
              label={`+${allSkills.length - 5} more`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Box>
    );
  };

  const filteredJobs = getFilteredJobs();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
          fontSize: "16px",
          color: "#666",
          textAlign: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}
        >
          Search Jobs
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Discover opportunities that match your skills and preferences
        </Typography>
      </Box>

      {/* Company Profiles Slider */}
      {companyProfiles.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Companies with Matching Jobs
          </Typography>
          <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1 }}>
            {companyProfiles.map((company) => (
              <Card
                key={company.id}
                sx={{
                  minWidth: 280,
                  cursor: "pointer",
                  "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
                }}
                onClick={() => handleCompanyClick(company)}
              >
                <CardActionArea>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 50,
                          height: 50,
                          bgcolor: "primary.main",
                          mr: 2,
                        }}
                      >
                        <BusinessIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {company.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {company.industry}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {company.description?.substring(0, 100)}...
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={`${company.job_count} jobs`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={
                          company.size
                            ? `${company.size}+ employees`
                            : "Company size not specified"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Paper>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search jobs, companies, or skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Function</InputLabel>
              <Select
                value={selectedFunction}
                onChange={(e) => setSelectedFunction(e.target.value)}
                label="Function"
              >
                <MenuItem value="">All Functions</MenuItem>
                {functions.map((func) => (
                  <MenuItem key={func.id} value={func.id}>
                    {func.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Employment Type</InputLabel>
              <Select
                value={selectedEmploymentType}
                onChange={(e) => setSelectedEmploymentType(e.target.value)}
                label="Employment Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="Full-time">Full-time</MenuItem>
                <MenuItem value="Part-time">Part-time</MenuItem>
                <MenuItem value="Contractor">Contractor</MenuItem>
                <MenuItem value="Freelancer">Freelancer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                label="Location"
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => {
                setSearchTerm("");
                setSelectedFunction("");
                setSelectedEmploymentType("");
                setSelectedLocation("");
              }}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Summary */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {filteredJobs.length} {filteredJobs.length === 1 ? "job" : "jobs"}{" "}
          found
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            icon={<FavoriteIcon />}
            label={`${favorites.size} saved`}
            variant="outlined"
            color="primary"
          />
        </Stack>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Job Cards */}
      {filteredJobs.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center" }}>
          <WorkIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No jobs found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or check back later for new
            opportunities.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredJobs.map((job) => (
            <Grid item xs={12} md={6} lg={4} key={job.id}>
              <StyledCard>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                  >
                    <CompanyAvatar sx={{ mr: 2 }}>
                      {getCompanyInitials(job.company_name)}
                    </CompanyAvatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {job.job_role_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {job.company_name}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          label={job.employment_type}
                          size="small"
                          color={getEmploymentTypeColor(job.employment_type)}
                        />
                        <Chip
                          icon={<LocationIcon />}
                          label={job.country_name}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                    <IconButton
                      onClick={() => handleFavorite(job.id)}
                      color={favorites.has(job.id) ? "primary" : "default"}
                    >
                      {favorites.has(job.id) ? (
                        <FavoriteIcon />
                      ) : (
                        <FavoriteBorderIcon />
                      )}
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {job.summary?.length > 120
                      ? `${job.summary.substring(0, 120)}...`
                      : job.summary}
                  </Typography>

                  {renderSkills(job.skills)}

                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Posted {new Date(job.created_at).toLocaleDateString()}
                    </Typography>
                    <Chip
                      label={job.job_function_name}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Box>
                </CardContent>

                <CardActions sx={{ pt: 0, px: 0 }}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleJobClick(job)}
                    sx={{ color: "primary.main" }}
                  >
                    View Details
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                </CardActions>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Job Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedJob && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CompanyAvatar sx={{ mr: 2 }}>
                    {getCompanyInitials(selectedJob.company_name)}
                  </CompanyAvatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedJob.job_role_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedJob.company_name}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => handleFavorite(selectedJob.id)}
                  color={favorites.has(selectedJob.id) ? "primary" : "default"}
                >
                  {favorites.has(selectedJob.id) ? (
                    <FavoriteIcon />
                  ) : (
                    <FavoriteBorderIcon />
                  )}
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Job Description
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {selectedJob.summary}
                  </Typography>

                  {selectedJob.skills && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Required Skills
                      </Typography>
                      <Grid container spacing={2}>
                        {/* {selectedJob.skills.selected_must_have_skills
                          ?.length > 0 && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="error"
                              sx={{ mb: 1 }}
                            >
                              Must Have:
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              {selectedJob.skills.selected_must_have_specific_skills.map(
                                (skill, index) => (
                                  <SkillChip
                                    key={index}
                                    label={`${skill.skill} (${skill.level})`}
                                    level={skill.level}
                                  />
                                )
                              )}
                            </Box>
                          </Grid>
                        )}
                        {selectedJob.skills.selected_should_have_specific_skills
                          ?.length > 0 && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="warning.main"
                              sx={{ mb: 1 }}
                            >
                              Should Have:
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              {selectedJob.skills.selected_should_have_specific_skills.map(
                                (skill, index) => (
                                  <SkillChip
                                    key={index}
                                    label={`${skill.skill} (${skill.level})`}
                                    level={skill.level}
                                  />
                                )
                              )}
                            </Box>
                          </Grid>
                        )}
                        {selectedJob.skills.selected_could_have_specific_skills
                          ?.length > 0 && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="success.main"
                              sx={{ mb: 1 }}
                            >
                              Nice to Have:
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              {selectedJob.skills.selected_could_have_specific_skills.map(
                                (skill, index) => (
                                  <SkillChip
                                    key={index}
                                    label={`${skill.skill} (${skill.level})`}
                                    level={skill.level}
                                  />
                                )
                              )}
                            </Box>
                          </Grid>
                        )} */}

                        {selectedJob.skills.selected_must_have_skills?.length >
                          0 && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="error"
                              sx={{ mb: 1 }}
                            >
                              Must Have:
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              {selectedJob.skills.selected_must_have_skills.map(
                                (skillGroup, index) => {
                                  const skillName = Object.keys(skillGroup)[0];
                                  const skillData = skillGroup[skillName];
                                  return (
                                    <SkillChip
                                      key={index}
                                      label={`${skillName} (${skillData.level})`}
                                      level={skillData.level}
                                    />
                                  );
                                }
                              )}
                            </Box>
                          </Grid>
                        )}

                        {selectedJob.skills.selected_should_have_skills
                          ?.length > 0 && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="warning.main"
                              sx={{ mb: 1 }}
                            >
                              Should Have:
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              {selectedJob.skills.selected_should_have_skills.map(
                                (skillGroup, index) => {
                                  const skillName = Object.keys(skillGroup)[0];
                                  const skillData = skillGroup[skillName];
                                  return (
                                    <SkillChip
                                      key={index}
                                      label={`${skillName} (${skillData.level})`}
                                      level={skillData.level}
                                    />
                                  );
                                }
                              )}
                            </Box>
                          </Grid>
                        )}

                        {selectedJob.skills.selected_could_have_skills?.length >
                          0 && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="success.main"
                              sx={{ mb: 1 }}
                            >
                              Nice to Have:
                            </Typography>
                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              {selectedJob.skills.selected_could_have_skills.map(
                                (skillGroup, index) => {
                                  const skillName = Object.keys(skillGroup)[0];
                                  const skillData = skillGroup[skillName];
                                  return (
                                    <SkillChip
                                      key={index}
                                      label={`${skillName} (${skillData.level})`}
                                      level={skillData.level}
                                    />
                                  );
                                }
                              )}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Job Details
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <WorkIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Function"
                          secondary={selectedJob.job_function_name}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ScheduleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Employment Type"
                          secondary={selectedJob.employment_type}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <LocationIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Location"
                          secondary={selectedJob.country_name}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Company"
                          secondary={selectedJob.company_name}
                        />
                      </ListItem>
                      {selectedJob.company_industry && (
                        <ListItem>
                          <ListItemIcon>
                            <TrendingUpIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Industry"
                            secondary={selectedJob.company_industry}
                          />
                        </ListItem>
                      )}
                      {selectedJob.company_size && (
                        <ListItem>
                          <ListItemIcon>
                            <BusinessIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Company Size"
                            secondary={`${selectedJob.company_size}+ employees`}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleCloseDialog}>Close</Button>
              <Button
                variant={hasApplied ? "outlined" : "contained"}
                startIcon={hasApplied ? <CheckIcon /> : <ApplyIcon />}
                onClick={() => handleApply(selectedJob.id)}
                disabled={hasApplied}
                sx={{
                  minWidth: 120,
                  ...(hasApplied && {
                    color: "success.main",
                    borderColor: "success.main",
                  }),
                }}
              >
                {hasApplied ? "Applied" : "Apply Now"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Company Jobs Dialog */}
      <Dialog
        open={companyJobsDialogOpen}
        onClose={handleCloseCompanyJobsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <BusinessIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedCompany?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCompany?.industry}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {selectedCompany?.description}
          </Typography>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Available Jobs (
            {
              jobs.filter((job) => job.corporate_id === selectedCompany?.id)
                .length
            }
            )
          </Typography>

          <Grid container spacing={2}>
            {jobs
              .filter((job) => job.corporate_id === selectedCompany?.id)
              .map((job) => (
                <Grid item xs={12} key={job.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {job.job_role_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {job.job_function_name} • {job.employment_type}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {job.summary}
                          </Typography>
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <Chip
                              label={job.country_name}
                              size="small"
                              icon={<LocationIcon />}
                            />
                            <Chip
                              label={job.employment_type}
                              size="small"
                              color="primary"
                            />
                          </Box>
                        </Box>
                        <Button
                          variant={
                            appliedJobs.has(job.id) ? "outlined" : "contained"
                          }
                          startIcon={
                            appliedJobs.has(job.id) ? (
                              <CheckIcon />
                            ) : (
                              <ApplyIcon />
                            )
                          }
                          onClick={() => handleApply(job.id)}
                          disabled={appliedJobs.has(job.id)}
                          size="small"
                          sx={{
                            minWidth: 100,
                            ...(appliedJobs.has(job.id) && {
                              color: "success.main",
                              borderColor: "success.main",
                            }),
                          }}
                        >
                          {appliedJobs.has(job.id) ? "Applied" : "Apply"}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompanyJobsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SearchJobs;
